import { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../components/api'

const usePonctualite = () => {
  const [activeTab, setActiveTab] = useState('etudiants');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [absencesEtudiants, setAbsencesEtudiants] = useState([]);
  const [absencesEnseignants, setAbsencesEnseignants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Données statiques qui ne changent pas souvent
  const [classes, setClasses] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [modules, setModules] = useState([]);
  const [etudiants, setEtudiants] = useState([]);
  const [enseignants, setEnseignants] = useState([]);
  
  const [selectedClasse, setSelectedClasse] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    classe: '',
    niveau: '',
    filiere: '',
    dateDebut: null,
    dateFin: null
  });

  // État du formulaire
  const [formData, setFormData] = useState({
    etudiant: '',
    enseignant: '',
    matiere: '',
    volume_horaire: '',
    motif: '',
    date_absence: new Date().toISOString().split('T')[0] // Date du jour par défaut
  });

  // Refs pour éviter les appels API doubles
  const staticDataLoadedRef = useRef(false);
  const loadingStaticDataRef = useRef(false);

  const resetForm = useCallback(() => {
    setFormData({
      etudiant: '',
      enseignant: '',
      matiere: '',
      volume_horaire: '',
      motif: '',
      date_absence: new Date().toISOString().split('T')[0] // Date du jour par défaut
    });
    setSelectedClasse('');
    setSelectedModule('');
    setIsEditing(false);
    setEditingId(null);
  }, []);

  // Chargement initial des données statiques (une seule fois avec protection)
  const loadStaticData = useCallback(async () => {
    if (staticDataLoadedRef.current || loadingStaticDataRef.current) {
      return;
    }

    loadingStaticDataRef.current = true;
    
    try {
      console.log('🔄 Chargement des données statiques...');
      
      const [classesRes, filieresRes, niveauxRes, enseignantsRes] = await Promise.all([
        api.get('/classe/get-all-classe/'),
        api.get('/filiere/liste-filieres/'),
        api.get('/niveau/liste-niveaux/'),
        api.get('/enseignant/liste-enseignant/')
      ]);

      setClasses(classesRes.data.results || classesRes.data || []);
      setFilieres(filieresRes.data.results || filieresRes.data || []);
      setNiveaux(niveauxRes.data.results || niveauxRes.data || []);
      setEnseignants(enseignantsRes.data.results || enseignantsRes.data || []);
      
      staticDataLoadedRef.current = true;
      setDataLoaded(true);
      
      console.log('✅ Données statiques chargées avec succès');
    } catch (error) {
      console.error('❌ Erreur lors du chargement des données statiques:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      loadingStaticDataRef.current = false;
    }
  }, []);

  // Chargement des absences (données dynamiques)
  const loadAbsences = useCallback(async () => {
    try {
      console.log('🔄 Chargement des absences...');
      
      const [absencesEtudiantsRes, absencesEnseignantsRes] = await Promise.all([
        api.get('/ponctualite/absences-etudiants/'),
        api.get('/ponctualite/absences-enseignants/')
      ]);

      const absencesEtudiants = absencesEtudiantsRes.data.data || [];
      const absencesEnseignants = absencesEnseignantsRes.data.data || [];
      
      console.log('🔍 Données étudiants reçues:', absencesEtudiants);
      console.log('🔍 Données enseignants reçues:', absencesEnseignants);
      
      setAbsencesEtudiants(absencesEtudiants);
      setAbsencesEnseignants(absencesEnseignants);
      
      console.log('✅ Absences chargées avec succès');
    } catch (error) {
      console.error('❌ Erreur lors du chargement des absences:', error);
      
      // Ne pas traiter les 404 comme des erreurs critiques
      if (error.response && error.response.status === 404) {
        setAbsencesEtudiants([]);
        setAbsencesEnseignants([]);
      } else {
        toast.error('Erreur lors du chargement des absences');
      }
    }
  }, []);

  // Fonctions pour charger les données spécifiques
  const fetchEtudiantsByClasse = useCallback(async (classeId) => {
    try {
      const response = await api.get(`/assiduite/etudiant-by-class/${classeId}/`);
      setEtudiants(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des étudiants:', error);
      toast.error('Erreur lors du chargement des étudiants');
    }
  }, []);

  const fetchModulesByClasse = useCallback(async (classeId) => {
    try {
      const response = await api.get(`/assiduite/classe/${classeId}/modules/`);
      setModules(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des modules:', error);
      toast.error('Erreur lors du chargement des modules');
    }
  }, []);

  // Actions
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      // Validation : le module est obligatoire
      if (!selectedModule) {
        toast.error('Veuillez sélectionner un module');
        return;
      }

      const baseEndpoint = activeTab === 'etudiants' 
        ? '/ponctualite/absences-etudiants/'
        : '/ponctualite/absences-enseignants/';
      
      const endpoint = isEditing ? `${baseEndpoint}${editingId}/` : baseEndpoint;
      const method = isEditing ? 'put' : 'post';

      const data = activeTab === 'etudiants' 
        ? {
            etudiant: parseInt(formData.etudiant),
            matiere: parseInt(selectedModule),
            volume_horaire: parseInt(formData.volume_horaire),
            motif: formData.motif || '',
            absence: formData.absence !== undefined ? formData.absence : true,
            presence: formData.presence !== undefined ? formData.presence : false,
            date_absence: formData.date_absence
          }
        : {
            enseignant: parseInt(formData.enseignant),
            matiere: parseInt(selectedModule),
            volume_horaire: parseInt(formData.volume_horaire),
            motif: formData.motif || '',
            absence: formData.absence !== undefined ? formData.absence : true,
            presence: formData.presence !== undefined ? formData.presence : false,
            date_absence: formData.date_absence
          };

      console.log('🔍 Frontend - Données à envoyer:', { endpoint, data, activeTab, method, isEditing });

      const response = await api[method](endpoint, data);
      
      if (response.data && (response.data.status === 'success' || response.status === 200)) {
        toast.success(isEditing ? 'Absence modifiée avec succès' : 'Absence enregistrée avec succès');
        setShowModal(false);
        loadAbsences();
        resetForm();
      } else {
        throw new Error('Réponse invalide du serveur');
      }
    } catch (error) {
      console.error('❌ Frontend - Erreur lors de l\'enregistrement:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'enregistrement de l\'absence');
    }
  }, [activeTab, formData, selectedModule, loadAbsences, resetForm, isEditing, editingId]);

  const handleDelete = useCallback((id) => {
    setItemToDelete(id);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!itemToDelete) return;
    
    setIsDeleting(true);
    try {
      const endpoint = activeTab === 'etudiants' 
        ? `/ponctualite/absences-etudiants/${itemToDelete}/`
        : `/ponctualite/absences-enseignants/${itemToDelete}/`;
      
      await api.delete(endpoint);
      toast.success('Absence supprimée avec succès');
      loadAbsences();
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  }, [itemToDelete, activeTab, loadAbsences]);

  const handleEdit = useCallback(async (absence) => {
    console.log('🔍 Début de l\'édition:', absence);
    
    // Formater la date pour l'input date (YYYY-MM-DD)
    const formatDateForInput = (dateString) => {
      if (!dateString) return new Date().toISOString().split('T')[0];
      return new Date(dateString).toISOString().split('T')[0];
    };
    
    // Définir le mode édition et l'ID AVANT tout le reste
    setIsEditing(true);
    setEditingId(absence.id);
    
    try {
      if (activeTab === 'etudiants') {
        // ÉTUDIANT: Classe → Étudiants + Modules → Sélections
        const classeLibelle = absence.matiere?.classe?.libelle || absence.classe_nom;
        const classeId = classes.find(c => c.libelle === classeLibelle)?.id;
        
        if (classeId) {
          setSelectedClasse(classeId.toString());
          
          // Charger étudiants et modules de cette classe
          await Promise.all([
            fetchEtudiantsByClasse(classeId),
            fetchModulesByClasse(classeId)
          ]);
          
          // Maintenant sélectionner étudiant et module
          setFormData({
            etudiant: absence.etudiant?.id ? absence.etudiant.id.toString() : '',
            enseignant: '',
            matiere: '',
            volume_horaire: absence.volume_horaire || '',
            motif: absence.motif || '',
            absence: absence.absence,
            presence: absence.presence,
            date_absence: formatDateForInput(absence.date_absence)
          });
          
          setSelectedModule(absence.matiere?.id ? absence.matiere.id.toString() : '');
        }
      } else {
        // ENSEIGNANT: Direct avec toutes les données déjà chargées
        setFormData({
          etudiant: '',
          enseignant: absence.enseignant?.id ? absence.enseignant.id.toString() : '',
          matiere: '',
          volume_horaire: absence.volume_horaire || '',
          motif: absence.motif || '',
          absence: absence.absence,
          presence: absence.presence,
          date_absence: formatDateForInput(absence.date_absence)
        });
        
        const classeLibelle = absence.matiere?.classe?.libelle || absence.classe_nom;
        const classeId = classes.find(c => c.libelle === classeLibelle)?.id;
        if (classeId) {
          setSelectedClasse(classeId.toString());
        }
        
        setSelectedModule(absence.matiere?.id ? absence.matiere.id.toString() : '');
      }
      
      console.log('✅ Données d\'édition préparées');
    } catch (error) {
      console.error('❌ Erreur lors du chargement des données pour édition:', error);
      toast.error('Erreur lors du chargement des données pour l\'édition');
    }
    
    // Ouvrir le modal
    setShowModal(true);
  }, [activeTab, classes, fetchEtudiantsByClasse, fetchModulesByClasse]);



  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, []);

  // Effects optimisés
  useEffect(() => {
    if (!staticDataLoadedRef.current && !loadingStaticDataRef.current) {
      loadStaticData();
    }
  }, [loadStaticData]);

  useEffect(() => {
    if (dataLoaded) {
      loadAbsences().finally(() => setLoading(false));
    }
  }, [dataLoaded, loadAbsences]);

  useEffect(() => {
    if (selectedClasse && activeTab === 'etudiants') {
      fetchEtudiantsByClasse(selectedClasse);
      fetchModulesByClasse(selectedClasse);
    } else if (!selectedClasse) {
      setEtudiants([]);
      setModules([]);
    }
  }, [selectedClasse, activeTab, fetchEtudiantsByClasse, fetchModulesByClasse]);

  return {
    // States
    activeTab,
    showModal,
    showDeleteModal,
    itemToDelete,
    isDeleting,
    isEditing,
    editingId,
    absencesEtudiants,
    absencesEnseignants,
    loading,
    classes,
    niveaux,
    filieres,
    modules,
    etudiants,
    enseignants,
    selectedClasse,
    selectedModule,
    currentPage,
    totalPages,
    showFilters,
    filters,
    formData,
    
    // Setters
    setActiveTab,
    setShowModal,
    setShowDeleteModal,
    setSelectedClasse,
    setSelectedModule,
    setCurrentPage,
    setShowFilters,
    setFilters,
    setFormData,
    
    // Actions
    handleSubmit,
    handleDelete,
    confirmDelete,
    handleEdit,
    formatDate,
    resetForm,
    loadAbsences
  };
};

export default usePonctualite; 